import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, finalize, map, of, switchMap, tap } from 'rxjs';
import { RoutesEnum } from 'src/app/configuration/enums/routes.enum';
import {
  FlowpaymentsStrategyController,
  resolvePaymentFlowStrategy,
} from 'src/app/configuration/flow-payments/flow-payments.strategy';
import { PaymentsFlow } from 'src/app/configuration/models/configuration-model';
import { CreateCompanyService } from 'src/app/configuration/services/create-company.service';
import { TokenService } from 'src/app/configuration/services/token.service';
import { PaymentLinkService } from 'src/app/features/payment-link/services/payment-link.service';
import { LoadingIdsEnum, LoadingTypeEnum } from 'src/app/models/loading.model';
import { FeatureFlagsFacadeService } from 'src/app/services/feature-flags-facade.service';
import { AppState } from '../..';
import {
  fetchCreateTheme,
  fetchSignInRequestFailure,
  processFastCheckoutToken,
  processSubdomainFlow,
  resolveAccessFlow,
  setLoadingActive,
  setLoadingDisabled,
} from './config.actions';
import { SplitIoService } from 'src/app/configuration/services/split-io.service';
import { InternalFeatureFlagEnum } from 'src/app/common/featureFlags';

@Injectable()
export class ConfigEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly store: Store<AppState>,
    private readonly createCompanyService: CreateCompanyService,
    private readonly tokenService: TokenService,
    private readonly router: Router,
    private readonly injector: Injector,
    private readonly paymentLinkService: PaymentLinkService,
    private readonly featureFlagFacade: FeatureFlagsFacadeService,
    private readonly splitIoService: SplitIoService
  ) {}

  fetchCreateTheme$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fetchCreateTheme),
      tap(() => {
        this.store.dispatch(
          setLoadingActive({
            loadingConfig: {
              id: LoadingIdsEnum.SPINNER,
              type: LoadingTypeEnum.InitializeBackground,
            },
          })
        );
      }),
      switchMap((response) => {
        return this.createCompanyService.handleCompanyCreation(response.url).pipe(
          map((result) => {
            if (!result.success) {
              return fetchSignInRequestFailure();
            }
            return resolveAccessFlow({ url: response.url });
          })
        );
      })
    );
  });

  processFastCheckoutToken$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(processFastCheckoutToken),
        switchMap((response) => {
          this.tokenService.hasToken(response.url);
          return this.tokenService.decodeToken().pipe(
            map((responseTokenData) => {
              this.paymentLinkService.setTokenResponse(responseTokenData);
              this.splitIoService
                .getFeaturesFlags()
                .pipe(
                  finalize(() => {
                    this.paymentLinkService.buildStrategyForPaymentLinkFlow();
                  })
                )
                .subscribe();
              return EMPTY;
            })
          );
        })
      );
    },
    { dispatch: false }
  );

  resolveAccessFlow$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(resolveAccessFlow),
      switchMap((response) => {
        if (this.featureFlagFacade.featureFlagEnabled(InternalFeatureFlagEnum.Maintenance)) {
          this.store.dispatch(setLoadingDisabled());
          this.router.navigate([RoutesEnum.MAINTENANCE]);
          return of();
        }
        const hasToken = this.tokenService.hasToken(response.url);
        if (hasToken) {
          this.splitIoService
            .getFeaturesFlags()
            .pipe(
              finalize(() => {
                this.paymentLinkService.buildStrategyForPaymentLinkFlow();
              })
            )
            .subscribe();
          return EMPTY;
        }
        return of(processSubdomainFlow());
      })
    );
  });

  processSubdomainFlow$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(processSubdomainFlow),
      switchMap(() => {
        const subdomainFlowResult = resolvePaymentFlowStrategy();
        if (subdomainFlowResult.domainExists) {
          const flowType = PaymentsFlow[subdomainFlowResult.flowType as keyof typeof PaymentsFlow];
          const strategyClass = FlowpaymentsStrategyController.get(flowType);
          const strategyInstance = this.injector.get(strategyClass!);
          strategyInstance.build();
          return EMPTY;
        }
        return of(fetchSignInRequestFailure());
      })
    );
  });
  fetchSignInRequestFailure$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fetchSignInRequestFailure),
      map(() => {
        this.router.navigate(['/not-found']);
        return setLoadingDisabled();
      })
    );
  });
}
